import { merge, has } from 'lodash';

class Sound {
  constructor(settings = {}) {
    this.settings = {
      name: '',
      path: '',
      enabled: true,
      muted: false,
      volume: 1,
      gradually: false,
      volumeEditable: true,
      currentlyPlaying: false,
    };

    this.updateSettings(settings);
    this.settings.originalVolume = this.settings.volume;

    try {
      this.audio = new Audio();

      this.setVolume(this.settings.volume);
      this.audio.preload = 'auto';

      this.audio.addEventListener('canplaythrough', () => {
        this.settings.loaded = true;

        if (this.settings.queue) {
          this.play();
        }
      });

      this.audio.addEventListener('error', (error) => {
        console.log(`Could not load audio ${this.audio.settings.name}`, error);
      });

      this.audio.addEventListener('ended', () => {
        this.reset();
      });

      this.audio.src = this.settings.path;
      this.audio.load();
    } catch (e) {
      console.error(e);
    }
  }

  async play(settings = {}) {
    const { audio } = this;
    const {
      loop,
      startAt,
      volume,
      gradually,
    } = settings;

    if (!this.settings.loaded) {
      this.queue = true;
    }

    if (this.settings.enabled && (audio.paused || audio.ended)) {
      this.reset();
      audio.loop = loop || false;

      if (startAt) {
        audio.currentTime = audio.duration - startAt;
        if (audio.currentTime < 0) {
          audio.currentTime = 0;
        }
      }

      if (volume) {
        this.setVolume(volume);
      }

      if (has(settings, 'gradually')) {
        this.settings.gradually = gradually;
      }

      if (this.settings.gradually) {
        this.gradually('increase');
      }

      audio.play().then((response) => {
        this.settings.currentlyPlaying = true;
      }).catch((error) => {
        this.settings.currentlyPlaying = false;
      });
    }
  }

  pause() {
    if (this.settings.enabled) {
      this.audio.pause();
    }
  }

  stop() {
    if (
      this.settings.enabled
      && this.settings.volumeEditable
      && !this.audio.paused
      && !this.audio.ended
    ) {
      if (this.settings.gradually) {
        this.gradually('decrease').then(() => {
          this.audio.pause();
          this.reset();
        });
      } else {
        this.audio.pause();
        this.reset();
      }
    }
  }

  gradually(what) {
    return new Promise((resolve) => {
      const { audio } = this;
      const { originalVolume } = this.settings;
      let finished = false;

      this.settings.volumeEditable = false;

      if (what === 'increase') {
        audio.volume = 0;
      }

      const self = this;
      this.interval = setInterval(() => {
        if (what === 'increase') {
          if (!this.settings.muted && audio.volume < originalVolume) {
            self.setVolume(audio.volume + 0.01);
          } else {
            finished = true;
          }
        } else if (what === 'decrease') {
          if (!this.settings.muted && audio.volume > 0) {
            self.setVolume(audio.volume - 0.01);
          } else {
            self.setVolume(0);
            finished = true;
          }
        }

        if (finished) {
          self.settings.volumeEditable = true;
          resolve();
          clearInterval(self.interval);
          self.interval = null;
        }
      }, 75);
    });
  }

  updateSettings(settings = {}) {
    merge(this.settings, settings);

    this.setVolume(this.settings.volume);
  }

  setVolume(volume) {
    // eslint-disable-next-line no-param-reassign
    if (volume < 0) volume = 0;
    // eslint-disable-next-line no-param-reassign
    if (volume > 1) volume = 1;

    if (this.audio) {
      this.audio.volume = volume;
    }
  }

  reset() {
    this.audio.currentTime = 0;
    this.audio.volume = !this.settings.muted ? this.settings.originalVolume : 0;
    this.queue = false;
    this.settings.volumeEditable = true;
  }

  destroy() {
    this.audio = null;
    this.settings.enabled = false;
  }
}

export default Sound;
